import './ConfirmEmail.css';
import '../common-style.css';

import { useContext, useEffect, useRef, useState } from 'react';
import { noWhitespaceTrim, validateInput } from 't-lib-ui/helpers';
import { confirmEmail, getCurrentAccount, getCachedEmail, getEmail } from '../../connector/auth';
import { finishConfirmedLogin } from './finish-confirm';
import { AppState } from '../../context/general';
import { useProgressModal } from 't-lib-ui/structure';
import { redirect, RouterContext, getLocation, goTo } from 't-lib-ui/router';
import { FlowForm, useFlowFormGlobal } from 't-lib-ui/structure';
import { Link } from 't-lib-ui/router';
import { ErrorNote } from 't-lib-ui/widgets';
import { TextButton } from 't-lib-ui/widgets';
import { Input } from 't-lib-ui/widgets';
import { FormGrid, FormGridLine } from 't-lib-ui/structure';
import { FetchError } from 't-lib-ui/connector';
import { SignupCodeLength } from '../../schema-copy/auth-schema';
import DownreadLogo from '../../components/DownreadLogo';

// The emailed sign-up code is SignupCodeLength digits (see aux-services/auth/handler/auth.ts).
function validateCode(value: string): 'missing'|'short'|'bad'|undefined {
  if (!value)
    return 'missing';
  if (value.length < SignupCodeLength)
    return 'short';
  return value.length > SignupCodeLength || !/^\d+$/.test(value) ? 'bad' : undefined;
}

const statusErrorMessages = {
  connection: <>Failed to connect to server. Please try again later.</>,
  invalid: <>Code invalid. Please check that you entered it correctly.</>,
  tryLater: <>Too many failed attempts. Please wait a while before retrying.</>
};

const codeErrorMessages = {
  short: <>The code is {SignupCodeLength} digits long.<br/>Please check.</>,
  bad: <>The code you have entered is not correct.<br/>Please check.</>,
  missing: <>Please enter the code you received via email.</>
};

const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  const appState = useContext(AppState);
  useFlowFormGlobal();

  const [presentLoading, dismissLoading] = useProgressModal();

  // Check for URL parameters
  const location = getLocation(routerCtx);
  const urlUserId = location.searchParams.get('userId');
  const urlConfirmationCode = location.searchParams.get('confirmationCode');
  const hasUrlParams = !!(urlUserId && urlConfirmationCode);
  // The welcome link carries the encrypted email blob in its fragment (#e=...) so confirming in a
  // browser without the signup's stored blob can still recover the email. Fragments never hit the
  // server, so we read it here from window.location and hand it to confirmEmail as a fallback.
  const urlEmailBlob = window.location.hash.startsWith('#e=')
    ? decodeURIComponent(window.location.hash.slice(3))
    : undefined;

  const [validationCode, setValidationCode] = useState('');
  const [codeError, setCodeError] = useState<'missing'|'bad'|'short'|undefined>(undefined);

  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<'ok'|'fields'|'invalid'|'failed'|'connection'|'tryLater'>('ok');

  // The email may be confirmed in a DIFFERENT browser (the user opens the link on their phone,
  // then comes back to this tab). This tab can't finish the login itself — the account's app user
  // and membership were created over there, and this browser has none of that locally — so once it
  // sees the account is confirmed it forwards to the login screen to re-authenticate cleanly.
  // (Confirming in THIS browser, via the button or the code field below, auto-logs in instead.)
  // We poll: immediately on load (covers a reload after confirming elsewhere), then every 10s for
  // 10 minutes. The check is GET /auth/account, served from the auth server's cookie cache
  // (invalidated by the confirm), so repeated polls cost at most one DB read — right after confirm.
  const navigatedRef = useRef(false);
  useEffect(() => {
    const auth = appState.state.authData;
    // Only the waiting session (this browser is logged in but unconfirmed) polls; the click-to-
    // confirm view (URL params, possibly no session) confirms directly via the button instead.
    if (!auth || hasUrlParams) return;

    let cancelled = false;
    const startedAt = Date.now();
    const POLL_INTERVAL_MS = 10_000;
    const MAX_DURATION_MS = 10 * 60_000;

    async function check() {
      if (cancelled || navigatedRef.current) return;
      try {
        const account = await getCurrentAccount(auth!);
        if (account.confirmed && !cancelled && !navigatedRef.current) {
          navigatedRef.current = true;
          // Prefill the email on the login screen so the user only needs their password. Best
          // effort: use the in-memory value, else decrypt the stored blob; skip if unavailable.
          const email = getCachedEmail() ?? await getEmail(auth!).catch(() => undefined);
          goTo(routerCtx, email ? `/login?email=${encodeURIComponent(email)}` : '/login', 'replace');
        }
      } catch { /* transient error — keep polling until the deadline */ }
    }

    check(); // immediate check: handles a reload after the email was already confirmed elsewhere
    const timer = setInterval(() => {
      if (Date.now() - startedAt >= MAX_DURATION_MS) { clearInterval(timer); return; }
      check();
    }, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [appState.state.authData, hasUrlParams, routerCtx]);

  if (!appState.state.authData && !hasUrlParams)
    return redirect(routerCtx, '/login');

  // A confirmed session normally has nothing to do here. But if we arrived via a confirmation LINK
  // (hasUrlParams), that link is for a specific account that may DIFFER from the one already logged in
  // (e.g. signed up on another device, then clicked the link on this already-logged-in browser) — so
  // don't bounce to /a; fall through and let the button confirm the link's account, switching to it.
  if (appState.state.authData?.status == 'confirmed' && !hasUrlParams)
    return redirect(routerCtx, '/a');

  // Digits only: strips whatever rides along when the code is pasted out of the email (spaces, a
  // stray line break) instead of failing validation on it.
  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').substring(0, SignupCodeLength);
    setValidationCode(digits);
    if (submitted) {
      validateInput(digits, validateCode, setCodeError);
    }
  };

  async function confirmEmailClicked() {
    setSubmitted(true);

    // Determine which code to use
    let codeToUse: string;
    if (hasUrlParams) {
      codeToUse = urlConfirmationCode!;
    } else {
      const trimmedCode = noWhitespaceTrim(validationCode);
      if (!validateInput(trimmedCode, validateCode, setCodeError)) {
        setStatus('fields');
        return;
      }
      codeToUse = trimmedCode;
    }

    presentLoading({message: 'Checking code...', timeout: 10000});
    try {
      // A confirmation LINK identifies its own account (urlUserId); confirm THAT one, even if a
      // different account is currently logged in (confirmEmail is self-authenticating: the server
      // keys off body.id + the code). Only the manual code-entry path (no url params) confirms the
      // current waiting session. Using appState here would wrongly re-target the logged-in account.
      const authToUse = hasUrlParams ? ({ id: urlUserId! } as any) : appState.state.authData;
      const authData = await confirmEmail(authToUse, codeToUse, false, urlEmailBlob);
      console.log('Email confirmation result:', authData);
      if (authData.status == 'confirmed') {
        await finishConfirmedLogin(appState, authData);
        dismissLoading();
        return redirect(routerCtx, '/a');
      }
      else {
        setStatus('invalid');
      }
    }
    catch (e) {
      console.log('Error confirming email: ' + e);
      const type = e instanceof FetchError ? e.type : 'Connection';
      if (type == 'LoginFailed') {
        setCodeError('bad');
        setStatus('invalid');
      }
      else if (type == 'LoginTimeout') {
        setStatus('tryLater');
        setCodeError('bad');
      }
      else
        setStatus('connection');
    }
    dismissLoading();
  }

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard confirmCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /></a>
          </div>
          <ErrorNote errorId={status} errorTexts={statusErrorMessages} />

          {hasUrlParams ? (
            <>
              <div className="authIntro">Click below to confirm your email address.</div>
              <FormGrid>
                <FormGridLine span={2} align='center' className='confirmSubmitRow'>
                  <TextButton label='Confirm Email' onClick={confirmEmailClicked} className='next' type='major'/>
                </FormGridLine>
              </FormGrid>
            </>
          ) : (
            <>
              <div className='confirmInfo'>Please enter the {SignupCodeLength}-digit confirmation code you received via email.</div>
              <FormGrid>
                <FormGridLine span={2} className='confirmInputRow'>
                  <Input
                    label="Code"
                    layout='singleCol'
                    errorId={codeError}
                    errorTexts={codeErrorMessages}
                    type="text"
                    enterKeyHint="enter"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={SignupCodeLength}
                    value={validationCode}
                    onInput={handleCodeChange}
                    className='codeInput'
                  />
                </FormGridLine>
                <FormGridLine span={2} align='center' className='confirmSubmitRow'>
                  <TextButton label='Confirm' onClick={confirmEmailClicked} className='next' type='major'/>
                </FormGridLine>
              </FormGrid>

              <div className="authLink">
                Didn&apos;t get a confirmation code? <Link to={'/forgot-password?no-password'}>Request a new confirmation code</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </FlowForm>
  );
};

export default Page;
